import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import BackToTop from "@/components/feature/BackToTop";
import { categories, comics, faqItems } from "@/mocks/comics";

const heroTagline = [
  { icon: "ri-sparkling-2-fill", text: "奇幻" },
  { icon: "ri-magic-fill", text: "魔法" },
  { icon: "ri-earth-fill", text: "异世界" },
  { icon: "ri-leaf-fill", text: "精灵" },
  { icon: "ri-ghost-fill", text: "妖怪" },
  { icon: "ri-goblet-fill", text: "神话" },
];

export default function Home() {
  const [faqOpen, setFaqOpen] = useState<number | null>(0);

  const featured = comics.slice(0, 4);
  const latest = comics.slice(4, 10);
  const hot = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar />

      {/* ===== 首屏 ===== */}
      <section className="relative flex min-h-[560px] items-center overflow-hidden pt-20 md:min-h-[640px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=ethereal%20fantasy%20realm%20with%20floating%20fairy%20spirits%2C%20magical%20glowing%20mushroom%20forest%20and%20ancient%20elven%20ruins%2C%20deep%20emerald%20and%20amber%20gold%20color%20palette%2C%20cinematic%20dark%20fantasy%20concept%20art%2C%20painterly%20high%20detail%20atmosphere&width=1600&height=900&seq=hero-fantasy&orientation=landscape"
            alt="韩国妖精漫画奇幻异世界魔幻森林场景"
            title="韩国妖精漫画奇幻异世界大全"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/85 via-background-50/60 to-background-50"></div>
        </div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-3xl">
            <p className="inline-flex items-center gap-2 rounded-full border border-primary-300/40 bg-background-50/60 px-4 py-1.5 text-sm text-primary-300 backdrop-blur">
              <i className="ri-sparkling-2-fill"></i>
              韩国妖精漫画奇幻 · 魔法 · 异世界 · 神话
            </p>
            <h1 className="mt-6 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl">
              走进妖精的世界，
              <br />
              <span className="text-primary-400">追更最新奇幻魔法</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-foreground-700 md:text-lg">
              聚合韩国妖精漫画奇幻、魔法、异世界、精灵、妖怪、神话等热门题材，点开即看世界观设定与最新话更新，方便快速找到你想追的那部妖精漫画。
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/category/isekai"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
              >
                <i className="ri-earth-fill"></i>
                开始追更
              </Link>
              <Link
                to="/category/fantasy"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50/70 px-6 py-3 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-500 transition-colors backdrop-blur"
              >
                <i className="ri-gallery-fill"></i>
                浏览分类
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              {heroTagline.map((t) => (
                <span
                  key={t.text}
                  className="inline-flex items-center gap-1.5 rounded-full bg-background-50/70 px-3.5 py-1.5 text-sm text-foreground-700 backdrop-blur"
                >
                  <i className={`${t.icon} text-accent-500`}></i>
                  {t.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== 六大分类 ===== */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm font-medium text-primary-500">CATEGORIES</p>
            <h2 className="mt-2 font-heading text-2xl font-bold md:text-3xl">题材分类</h2>
          </div>
          <Link
            to="/category/fantasy"
            className="hidden items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors sm:inline-flex"
          >
            全部分类 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group relative flex items-center gap-4 overflow-hidden rounded-lg border border-background-200/70 bg-background-100/70 p-4 transition-all hover:-translate-y-0.5"
            >
              <div className="relative h-16 w-20 overflow-hidden rounded-md shrink-0 md:h-20 md:w-28">
                <img
                  src={c.cover}
                  alt={`${c.name}分类封面`}
                  title={`${c.name} 妖精漫画分类`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <i className={`${c.icon} text-primary-500`}></i>
                  <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors">
                    {c.name}
                  </h3>
                </div>
                <p className="mt-1 line-clamp-2 text-sm leading-5 text-foreground-600">{c.desc}</p>
                <p className="mt-1.5 text-xs text-accent-700">{c.count} 部作品</p>
              </div>
              <i className="ml-auto text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-primary-500" />
            </Link>
          ))}
        </div>
      </section>

      {/* ===== 精选推荐 ===== */}
      <section className="border-y border-background-200/60 bg-background-100/50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">FEATURED</p>
              <h2 className="mt-2 font-heading text-2xl font-bold md:text-3xl">编辑精选推荐</h2>
            </div>
            <Link
              to="/category/fantasy"
              className="hidden items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors sm:inline-flex"
            >
              更多精选 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {featured.map((c) => (
              <ComicCard key={c.id} comic={c} featured />
            ))}
          </div>
        </div>
      </section>

      {/* ===== 最新更新 ===== */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm font-medium text-primary-500">LATEST</p>
            <h2 className="mt-2 font-heading text-2xl font-bold md:text-3xl">最新话更新</h2>
          </div>
        </div>
        <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {latest.map((c) => (
            <ComicCard key={c.id} comic={c} />
          ))}
        </div>
      </section>

      {/* ===== 热门榜单 ===== */}
      <section className="border-y border-background-200/60 bg-background-100/50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm font-medium text-accent-600">HOT</p>
              <h2 className="mt-2 font-heading text-2xl font-bold md:text-3xl">本周热门妖精漫画</h2>
            </div>
            <span className="hidden items-center gap-1 text-sm text-foreground-600 sm:inline-flex">
              按读者评分排序
            </span>
          </div>
          <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {hot.map((c, i) => (
              <Link
                key={c.id}
                to={`/comic/${c.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-3 transition-all hover:-translate-y-0.5 hover:border-primary-300"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 font-heading text-lg font-black text-secondary-900">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-500 transition-colors">
                    {c.title}
                  </h3>
                  <p className="mt-1 flex items-center gap-3 text-xs text-foreground-600">
                    <span className="text-accent-700">{c.category}</span>
                    <span><i className="ri-star-fill text-primary-500"></i> {c.rating.toFixed(1)}</span>
                    <span>{c.chapters} 话</span>
                  </p>
                </div>
                <i className="text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-primary-500" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="mx-auto max-w-4xl px-4 md:px-6 py-12 md:py-16">
        <div className="text-center">
          <p className="text-sm font-medium text-primary-500">FAQ</p>
          <h2 className="mt-2 font-heading text-2xl font-bold md:text-3xl">常见问题</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-foreground-600">
            关于韩国妖精漫画、追更方式与投稿推荐的疑问，这里都有答案。
          </p>
        </div>
        <div className="mt-8 space-y-3">
          {faqItems.map((f, idx) => (
            <div key={f.q} className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100/60">
              <button
                type="button"
                onClick={() => setFaqOpen(faqOpen === idx ? null : idx)}
                className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                aria-expanded={faqOpen === idx}
              >
                <span className="font-heading text-sm font-bold text-foreground-950 md:text-base">{f.q}</span>
                <i
                  className={`text-lg text-primary-500 transition-transform ${faqOpen === idx ? "rotate-180" : ""}`}
                ></i>
              </button>
              {faqOpen === idx && (
                <p className="border-t border-background-200/60 px-5 py-4 text-sm leading-7 text-foreground-700">
                  {f.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=wide%20panorama%20of%20a%20fantasy%20fairy%20realm%20with%20floating%20lights%20and%20ancient%20ruins%2C%20dark%20emerald%20and%20amber%20gold%20palette%2C%20cinematic%20mystical%20atmosphere&width=1600&height=500&seq=cta-fantasy&orientation=landscape"
            alt="韩国妖精漫画奇幻场景横幅"
            title="追更韩国妖精漫画"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/40"></div>
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-xl">
            <h2 className="font-heading text-2xl font-bold md:text-4xl">别错过每一话的精彩</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              从奇幻异世界到精灵妖怪神话，收藏你的追更列表，第一时间看到最新更新与世界观解析。
            </p>
            <Link
              to="/category/fantasy"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
            >
              <i className="ri-bookmark-fill"></i>
              收藏追更
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
}